// Ported from peerception-excel-add-in-modelling (originally the webapp's refreshAuth.ts).
// Refresh-token rotation with single-flight + reuse-detection awareness.
// Only change vs the webapp: BASE_URL comes from import.meta.env.VITE_API_URL.
import { readToken, writeToken, clearTokens } from './tokenStore';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';
const REFRESH_PATH = '/token/refresh';
const REFRESH_COOKIE_DAYS = 30;

let inFlight: Promise<boolean> | null = null;
let accessTokenAtRefreshStart: string | null = null;

export function getAccessToken(): string | null {
  return readToken('accessToken');
}

export function getRefreshToken(): string | null {
  return readToken('refreshToken');
}

export function setAuthCookies(args: {
  access_token: string;
  token_type: string;
  refresh_token?: string | null;
}): void {
  writeToken('accessToken', args.access_token, REFRESH_COOKIE_DAYS);
  writeToken('tokenType', args.token_type, REFRESH_COOKIE_DAYS);
  if (args.refresh_token) {
    writeToken('refreshToken', args.refresh_token, REFRESH_COOKIE_DAYS);
  }
}

export function clearAuth(): void {
  clearTokens();
}

async function doRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  // If a sibling context already rotated since we last saw the access token, adopt it.
  const currentAccess = getAccessToken();
  if (
    accessTokenAtRefreshStart !== null &&
    currentAccess !== null &&
    currentAccess !== accessTokenAtRefreshStart
  ) {
    return true;
  }

  const response = await fetch(`${BASE_URL}${REFRESH_PATH}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  }).catch(() => null);

  if (!response || !response.ok) return false;

  try {
    const data = await response.json();
    setAuthCookies({
      access_token: data.access_token,
      token_type: data.token_type,
      refresh_token: data.refresh_token,
    });
    return true;
  } catch {
    return false;
  }
}

export function refreshAccessToken(): Promise<boolean> {
  if (inFlight) return inFlight;
  accessTokenAtRefreshStart = getAccessToken();
  inFlight = doRefresh().finally(() => {
    inFlight = null;
    accessTokenAtRefreshStart = null;
  });
  return inFlight;
}

// The refresh token alone is enough for the API to revoke the session, so a sign-out after the access token expired
// still closes it on the server; the bearer goes along when there is one.
export async function logout(): Promise<void> {
  const accessToken = readToken('accessToken');
  const tokenType = readToken('tokenType');
  const refreshToken = getRefreshToken();
  const bearer: Record<string, string> =
    accessToken && tokenType ? { Authorization: `${tokenType} ${accessToken}` } : {};
  if (refreshToken || bearer.Authorization) {
    await fetch(`${BASE_URL}/logout`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        ...bearer,
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    }).catch(() => null);
  }
  clearTokens();
}
